import React, { useEffect, useState } from "react";
import { PageHeader, Card, Badge, Button, Spinner, Input, Select } from "@/components/ui";
import { endpoints } from "@/lib/api";
import { fmt } from "@/lib/format";
import { confirmCost } from "@/lib/confirmCost";
import { Sparkles, Plus, X, Zap, CheckCircle2, AlertCircle, Layers, TrendingUp, Target as TargetIcon, ArrowRight, Flame, History, ArrowLeftRight } from "lucide-react";
import { Link } from "react-router-dom";
import { BulkDmButton } from "@/components/BulkDmButton";

const STRATEGIES = [
  { id: "buy_and_build", name: "Buy & Build" },
  { id: "distressed", name: "Distressed" },
  { id: "undervalued", name: "Undervalued" },
  { id: "consolidation", name: "Consolidation" },
];

const PRESET_TEMPLATES = [
  { name: "Family-Owned Food PMI", theses: [
    { sector: "food processing", country: "Italy", revenue_max_mn: 60, strategy: "buy_and_build", theme: "family succession" },
    { sector: "artisan bakery", country: "Italy", revenue_max_mn: 40, strategy: "buy_and_build", theme: "family succession" },
    { sector: "wine & spirits", country: "Italy", revenue_max_mn: 80, strategy: "buy_and_build", theme: "consolidation" },
  ]},
  { name: "Italian Industrial Roll-Up", theses: [
    { sector: "industrial automation", country: "Italy", revenue_max_mn: 80, strategy: "consolidation" },
    { sector: "packaging machinery", country: "Italy", revenue_max_mn: 100, strategy: "consolidation" },
    { sector: "precision manufacturing", country: "Italy", revenue_max_mn: 60, strategy: "buy_and_build" },
    { sector: "specialty chemicals", country: "Italy", revenue_max_mn: 100, strategy: "consolidation" },
  ]},
  { name: "Luxury & Fashion Bolt-Ons", theses: [
    { sector: "luxury leather goods", country: "Italy", revenue_max_mn: 60, strategy: "buy_and_build" },
    { sector: "premium eyewear", country: "Italy", revenue_max_mn: 80, strategy: "buy_and_build" },
    { sector: "high-end jewellery", country: "Italy", revenue_max_mn: 50, strategy: "consolidation" },
  ]},
  { name: "Healthcare & Services", theses: [
    { sector: "medical devices", country: "Italy", revenue_max_mn: 80, strategy: "buy_and_build" },
    { sector: "dental clinic network", country: "Italy", revenue_max_mn: 60, strategy: "consolidation" },
    { sector: "veterinary chain", country: "Italy", revenue_max_mn: 40, strategy: "buy_and_build" },
  ]},
  { name: "Tech & Software PMI", theses: [
    { sector: "vertical SaaS", country: "Italy", revenue_max_mn: 30, strategy: "buy_and_build" },
    { sector: "MSP / IT services", country: "Italy", revenue_max_mn: 40, strategy: "consolidation" },
    { sector: "cybersecurity", country: "Italy", revenue_max_mn: 30, strategy: "buy_and_build" },
  ]},
];

const EMPTY_THESIS = { sector: "", country: "Italy", revenue_max_mn: 60, strategy: "buy_and_build", theme: "" };

export default function BulkDiscovery() {
  const [theses, setTheses] = useState([{ ...EMPTY_THESIS, sector: "food processing" }]);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [promoting, setPromoting] = useState(false);
  const [promoteResult, setPromoteResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [compareA, setCompareA] = useState("");
  const [compareB, setCompareB] = useState("");
  const [compareResult, setCompareResult] = useState(null);
  const [comparing, setComparing] = useState(false);

  const loadHistory = async () => {
    try {
      const r = await endpoints.bulkHistory();
      setHistory(r.data || []);
    } catch {}
  };

  useEffect(() => { loadHistory(); }, []);

  const runCompare = async () => {
    if (!compareA || !compareB || compareA === compareB) return;
    setComparing(true);
    setCompareResult(null);
    try {
      const r = await endpoints.bulkCompare(compareA, compareB);
      setCompareResult(r.data);
    } catch (e) {
      setCompareResult({ error: e?.response?.data?.detail || e.message });
    } finally {
      setComparing(false);
    }
  };

  const loadPreset = (p) => {
    setTheses(p.theses.map((t) => ({ ...EMPTY_THESIS, ...t })));
    setResult(null);
    setError("");
  };

  const addThesis = () => {
    if (theses.length >= 10) return;
    setTheses([...theses, { ...EMPTY_THESIS }]);
  };

  const removeThesis = (idx) => {
    setTheses(theses.filter((_, i) => i !== idx));
  };

  const updateThesis = (idx, patch) => {
    setTheses(theses.map((t, i) => (i === idx ? { ...t, ...patch } : t)));
  };

  const runAll = async () => {
    const valid = theses.filter((t) => t.sector?.trim());
    if (valid.length === 0) {
      setError("Add at least one thesis with a sector.");
      return;
    }
    const confirmed = await confirmCost({
      op: "discover", units: valid.length,
      title: `Run ${valid.length} parallel origination theses?`,
      items: valid.map((t, i) => `#${i + 1} — ${t.sector}${t.theme ? ` · ${t.theme}` : ""}${t.country ? ` · ${t.country}` : ""}`),
      llm_calls: valid.length * 2,
      web_calls: valid.length * 1,
      estimated_seconds: 90,
    });
    if (!confirmed) return;
    setRunning(true);
    setError("");
    setResult(null);
    setPromoteResult(null);
    try {
      const body = { theses: valid.map((t) => ({ ...t, revenue_max_mn: Number(t.revenue_max_mn) || 60 })), dedup: true };
      const r = await endpoints.discoverBulk(body);
      setResult(r.data);
      loadHistory();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setRunning(false);
    }
  };

  const convergentIds = (result?.aggregated_targets || [])
    .filter((t) => (t.matched_theses?.length || 0) >= 2 && t.company_id)
    .map((t) => t.company_id);
  const highConvergentIds = (result?.aggregated_targets || [])
    .filter((t) => (t.matched_theses?.length || 0) >= 3 && t.company_id)
    .map((t) => t.company_id);

  const promote = async () => {
    if (convergentIds.length === 0) return;
    setPromoting(true);
    setPromoteResult(null);
    try {
      const r = await endpoints.promoteToDiligence({
        company_ids: convergentIds,
        note: `Bulk promote from ${result.theses_count} theses`,
      });
      setPromoteResult(r.data);
    } catch (e) {
      setPromoteResult({ error: e?.response?.data?.detail || e.message });
    } finally {
      setPromoting(false);
    }
  };

  return (
    <div className="p-4 md:p-10 max-w-7xl mx-auto">
      <PageHeader
        eyebrow="CLARA Origin · Bulk"
        title={<>Bulk <span className="italic text-gold">Discovery</span> Engine</>}
        description="Run 2-10 origination theses in parallel. Targets are aggregated across theses with a match-count so convergent picks (matched by multiple lenses) rise to the top."
        testid="bulk-discovery-page"
        actions={
          <Link to="/origin">
            <Button variant="outline" data-testid="back-to-origin">
              <TargetIcon size={14} /> Back to Origin
            </Button>
          </Link>
        }
      />

      {/* Preset templates */}
      <Card className="mb-6">
        <div className="overline mb-3">Preset Playbooks</div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          {PRESET_TEMPLATES.map((p) => (
            <button
              key={p.name}
              onClick={() => loadPreset(p)}
              data-testid={`preset-${p.name.replace(/\s/g, "-")}`}
              className="text-left p-3 rounded-md bg-white/[0.02] border border-white/5 hover:border-[#C5A059]/40 transition"
            >
              <div className="text-[11.5px] font-medium text-white/85 leading-snug">{p.name}</div>
              <div className="text-[10px] text-white/40 mt-1">{p.theses.length} theses</div>
            </button>
          ))}
        </div>
      </Card>

      {/* Thesis builder */}
      <Card className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="overline">Active Theses ({theses.length}/10)</div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={addThesis} disabled={theses.length >= 10} data-testid="add-thesis-btn">
              <Plus size={13} /> Add Thesis
            </Button>
            <Button variant="gold" onClick={runAll} disabled={running || theses.length === 0} data-testid="run-bulk-btn">
              {running ? <><Spinner size={13} /> Running…</> : <><Zap size={13} /> Run All in Parallel</>}
            </Button>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 rounded-md text-sm text-red-400" style={{ background: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.2)" }}>
            <AlertCircle size={14} className="inline mr-1.5" />
            {error}
          </div>
        )}

        <div className="space-y-2">
          {theses.map((t, i) => (
            <div key={i} data-testid={`thesis-row-${i}`} className="grid grid-cols-12 gap-2 items-center p-2 rounded-md bg-white/[0.02] border border-white/5">
              <div className="col-span-1 text-center text-gold font-mono text-xs">T{i + 1}</div>
              <div className="col-span-4">
                <Input value={t.sector} onChange={(e) => updateThesis(i, { sector: e.target.value })} placeholder="Sector (e.g. food processing)" data-testid={`thesis-sector-${i}`} />
              </div>
              <div className="col-span-2">
                <Input value={t.country} onChange={(e) => updateThesis(i, { country: e.target.value })} placeholder="Country" data-testid={`thesis-country-${i}`} />
              </div>
              <div className="col-span-1">
                <Input type="number" value={t.revenue_max_mn} onChange={(e) => updateThesis(i, { revenue_max_mn: e.target.value })} placeholder="€M max" data-testid={`thesis-rev-${i}`} />
              </div>
              <div className="col-span-2">
                <Select value={t.strategy} onChange={(e) => updateThesis(i, { strategy: e.target.value })} data-testid={`thesis-strategy-${i}`}>
                  {STRATEGIES.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </Select>
              </div>
              <div className="col-span-1">
                <Input value={t.theme || ""} onChange={(e) => updateThesis(i, { theme: e.target.value })} placeholder="theme" data-testid={`thesis-theme-${i}`} />
              </div>
              <div className="col-span-1 flex justify-end">
                <button
                  onClick={() => removeThesis(i)}
                  disabled={theses.length === 1}
                  data-testid={`remove-thesis-${i}`}
                  className="p-1.5 text-white/40 hover:text-red-400 disabled:opacity-30"
                >
                  <X size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Results */}
      {result && (
        <>
          {highConvergentIds.length > 0 && (
            <div
              className="mb-5 p-4 rounded-md flex items-start gap-3"
              style={{ background: "rgba(197,160,89,0.08)", border: "1px solid rgba(197,160,89,0.4)" }}
              data-testid="convergent-alert"
            >
              <Flame size={18} className="text-gold shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="text-[13px] font-medium text-white">
                  Convergent Targets Detected — <span className="text-gold">{highConvergentIds.length}</span> target(s) surfaced by ≥3 theses
                </div>
                <div className="text-[11.5px] text-white/60 mt-0.5">
                  Multi-lens convergence is a strong institutional signal. IC pre-read recommended.
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5" data-testid="bulk-summary">
            <div className="p-3 clara-surface-2 rounded-md">
              <div className="text-[10px] uppercase tracking-wider text-white/40 mb-1 flex items-center gap-1"><Layers size={11} /> Theses Run</div>
              <div className="text-2xl font-display text-white">{result.theses_count}</div>
            </div>
            <div className="p-3 clara-surface-2 rounded-md">
              <div className="text-[10px] uppercase tracking-wider text-white/40 mb-1 flex items-center gap-1"><TargetIcon size={11} /> Unique Targets</div>
              <div className="text-2xl font-display text-gold">{result.aggregated_count}</div>
            </div>
            <div className="p-3 clara-surface-2 rounded-md">
              <div className="text-[10px] uppercase tracking-wider text-white/40 mb-1 flex items-center gap-1"><CheckCircle2 size={11} className="text-emerald-400" /> Saved to DB</div>
              <div className="text-2xl font-display text-white">{result.total_inserted}</div>
            </div>
            <div className="p-3 clara-surface-2 rounded-md">
              <div className="text-[10px] uppercase tracking-wider text-white/40 mb-1 flex items-center gap-1"><TrendingUp size={11} className="text-gold" /> Web Evidence</div>
              <div className="text-2xl font-display text-white">{result.total_web_evidence_sources}</div>
            </div>
          </div>

          <Card>
            <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
              <div className="overline">Aggregated Target Universe</div>
              <div className="flex items-center gap-2">
                {promoteResult && !promoteResult.error && (
                  <span className="text-[11px] text-emerald-400" data-testid="promote-result">
                    ✓ {promoteResult.promoted} promoted · {promoteResult.created} new deals · {promoteResult.already_downstream} already downstream
                  </span>
                )}
                {promoteResult?.error && (
                  <span className="text-[11px] text-red-400">Error: {promoteResult.error}</span>
                )}
                {result.snapshot_id && <BulkDmButton snapshotId={result.snapshot_id} />}
                <Button
                  variant="gold"
                  onClick={promote}
                  disabled={promoting || convergentIds.length === 0}
                  data-testid="promote-to-diligence-btn"
                >
                  {promoting ? <><Spinner size={12} /> Promoting…</> : (
                    <><ArrowRight size={13} /> Convert {convergentIds.length} to DD</>
                  )}
                </Button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-[12px]">
                <thead className="text-[10px] uppercase tracking-wider text-white/40 border-b border-white/5">
                  <tr>
                    <th className="text-left py-2 pl-2">Company</th>
                    <th className="text-left">Sector</th>
                    <th className="text-left">Country</th>
                    <th className="text-right">Revenue</th>
                    <th className="text-right">EBITDA</th>
                    <th className="text-center">Matched</th>
                    <th className="text-center">Confidence</th>
                    <th className="text-left pl-3">Sources</th>
                  </tr>
                </thead>
                <tbody>
                  {result.aggregated_targets.map((t, i) => {
                    const matchCount = t.matched_theses?.length || 0;
                    const isHighConvergent = matchCount >= 3;
                    const isConvergent = matchCount >= 2;
                    return (
                      <tr
                        key={i}
                        data-testid={`bulk-target-${i}`}
                        className={`border-b border-white/[0.04] transition ${
                          isHighConvergent
                            ? "bg-[#C5A059]/[0.08] hover:bg-[#C5A059]/[0.12]"
                            : isConvergent
                            ? "bg-white/[0.03] hover:bg-white/[0.05]"
                            : "hover:bg-white/[0.02]"
                        }`}
                        style={isHighConvergent ? { boxShadow: "inset 2px 0 0 #C5A059" } : undefined}
                      >
                        <td className="py-2 pl-2 text-white font-medium flex items-center gap-1.5">
                          {isHighConvergent && <Flame size={12} className="text-gold shrink-0" />}
                          {t.name}
                        </td>
                        <td className="text-white/70">{t.sector}</td>
                        <td className="text-white/60">{t.country}</td>
                        <td className="text-right text-white/70 font-mono">{fmt.money(t.revenue_eur, 0)}</td>
                        <td className="text-right text-white/70 font-mono">{t.ebitda_eur ? fmt.money(t.ebitda_eur, 0) : "—"}</td>
                        <td className="text-center">
                          {isHighConvergent ? (
                            <Badge tone="gold" data-testid={`convergent-badge-${i}`}>×{matchCount} 🔥</Badge>
                          ) : matchCount > 1 ? (
                            <Badge tone="gold">×{matchCount}</Badge>
                          ) : (
                            <Badge tone="neutral">1</Badge>
                          )}
                        </td>
                        <td className="text-center">
                          <Badge tone={t.confidence === "high" ? "green" : t.confidence === "medium" ? "gold" : "neutral"}>
                            {t.confidence || "—"}
                          </Badge>
                        </td>
                        <td className="text-white/50 text-[10px] pl-3">{(t._sources || []).join(", ") || "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>

          {/* Per-thesis breakdown */}
          <div className="mt-5">
            <div className="overline mb-2">Per-Thesis Breakdown</div>
            <div className="grid gap-2">
              {result.per_thesis.map((pt) => (
                <div key={pt.thesis_index} data-testid={`thesis-result-${pt.thesis_index}`} className="p-3 rounded-md bg-white/[0.02] border border-white/5 flex items-center gap-3 text-[12px]">
                  <span className="text-gold font-mono w-8">T{pt.thesis_index + 1}</span>
                  <span className="text-white/80 flex-1">{pt.thesis.sector} · {pt.thesis.country}</span>
                  {pt.error ? (
                    <Badge tone="red">error</Badge>
                  ) : (
                    <>
                      <Badge tone="neutral">{pt.target_count} targets</Badge>
                      <Badge tone="green">+{pt.inserted_count} saved</Badge>
                      {(pt.sources_used || []).includes("tavily") && <Badge tone="gold">tavily</Badge>}
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Bulk History */}
      {history.length > 0 && (
        <Card className="mt-8" data-testid="bulk-history-card">
          <div className="flex items-center justify-between mb-3">
            <div className="overline flex items-center gap-2"><History size={12} /> Recent Bulk Runs ({history.length})</div>
            <div className="flex items-center gap-2 text-[11px] text-white/50">
              <ArrowLeftRight size={12} /> Compare 2 runs:
              <select value={compareA} onChange={(e) => setCompareA(e.target.value)} data-testid="compare-a" className="bg-white/[0.03] border border-white/10 rounded px-2 py-1 text-white">
                <option value="">— A —</option>
                {history.map((h) => (
                  <option key={h.id} value={h.id}>{new Date(h.created_at).toLocaleString()} · {h.aggregated_count} targets</option>
                ))}
              </select>
              <select value={compareB} onChange={(e) => setCompareB(e.target.value)} data-testid="compare-b" className="bg-white/[0.03] border border-white/10 rounded px-2 py-1 text-white">
                <option value="">— B —</option>
                {history.map((h) => (
                  <option key={h.id} value={h.id}>{new Date(h.created_at).toLocaleString()} · {h.aggregated_count} targets</option>
                ))}
              </select>
              <Button variant="outline" onClick={runCompare} disabled={!compareA || !compareB || compareA === compareB || comparing} data-testid="run-compare-btn">
                {comparing ? <Spinner size={11} /> : <><ArrowLeftRight size={11} /> Diff</>}
              </Button>
            </div>
          </div>

          <div className="space-y-1.5 mb-3 max-h-56 overflow-y-auto">
            {history.map((h) => (
              <div key={h.id} data-testid={`history-row-${h.id.slice(0,8)}`} className="flex items-center gap-3 p-2.5 rounded bg-white/[0.02] border border-white/5 text-[12px]">
                <span className="text-white/50 font-mono text-[10px] w-32 shrink-0">{new Date(h.created_at).toLocaleString()}</span>
                <span className="flex-1 text-white/80 truncate">{h.thesis_labels.slice(0,4).join(" · ") + (h.theses_count > 4 ? " · …" : "")}</span>
                <Badge tone="neutral">{h.theses_count} theses</Badge>
                <Badge tone="gold">{h.aggregated_count} targets</Badge>
                <Badge tone="green">+{h.total_inserted} saved</Badge>
                {h.convergent_count > 0 && <Badge tone="gold"><Flame size={9} className="inline mr-0.5" /> {h.convergent_count}</Badge>}
                <BulkDmButton snapshotId={h.id} compact />
              </div>
            ))}
          </div>

          {compareResult && !compareResult.error && (
            <div className="mt-4 p-3 rounded-md bg-white/[0.02] border border-white/5" data-testid="compare-result">
              <div className="overline mb-2">Comparison Diff</div>
              <div className="grid grid-cols-3 gap-3 mb-3 text-[12px]">
                <div className="p-2 rounded bg-emerald-500/[0.06] border border-emerald-500/20">
                  <div className="text-[10px] text-emerald-400 uppercase mb-1">Only in A (dropped)</div>
                  <div className="text-2xl font-display text-white">{compareResult.only_a_count}</div>
                </div>
                <div className="p-2 rounded bg-[#C5A059]/[0.08] border border-[#C5A059]/25">
                  <div className="text-[10px] text-gold uppercase mb-1">Only in B (new)</div>
                  <div className="text-2xl font-display text-white">{compareResult.only_b_count}</div>
                </div>
                <div className="p-2 rounded bg-white/[0.03] border border-white/10">
                  <div className="text-[10px] text-white/50 uppercase mb-1">Shared</div>
                  <div className="text-2xl font-display text-white">{compareResult.shared_count}</div>
                </div>
              </div>
              {compareResult.only_b.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase text-gold/80 mb-1">New Targets Emerged in B</div>
                  <div className="flex flex-wrap gap-1.5">
                    {compareResult.only_b.slice(0, 20).map((t, i) => (
                      <span key={i} className="text-[11px] px-2 py-0.5 rounded bg-[#C5A059]/10 border border-[#C5A059]/30 text-white/80">
                        {t.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          {compareResult?.error && (
            <div className="mt-4 text-[12px] text-red-400">Compare error: {compareResult.error}</div>
          )}
        </Card>
      )}
    </div>
  );
}
